import React, {
  FC,
  MutableRefObject,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Alert, Button } from 'antd';
import { ImageOff, Minus, Play, Plus } from 'lucide-react';
import cx from 'classnames';
import Loader from '../../../components/loader';
import WhyModal from '../../../../clients/sidebar/components/screenshots/whyModal';
import ScreenshotsUpgrade from '../../../../clients/sidebar/components/screenshots/ScreenshotsUpgrade';
import Video from '../../../../clients/sidebar/components/screenshots/Video';
import {
  FailedScreenshotData,
  Props as ScreenshotProps
} from '../../../../clients/sidebar/components/screenshots';
import { useScreenshotErrorState } from '../../../../../clients/sidebar/views/hooks/useScreenshotErrorState';
import { HandleRegenerateScreenshotProps } from '../../../hooks/useScreenshotView';
import {
  getLangKey,
  getScreenshotServerUrl
} from '../../../models/Application';
import * as screenshotStrings from '../../../../clients/sidebar/components/screenshots/strings';
import SessionReplay from '../../../../../clients/shared/SessionReplay';
import styles from './index.module.css';

// @ts-ignore
const strings = screenshotStrings[getLangKey()];

const MIN_ZOOM = 50;
const MAX_ZOOM = 300;
const ZOOM_STEP = 25;
const DEFAULT_ZOOM = 100;

type MediaId = 'screenshot' | 'session';

type Props = {
  screenshot: ScreenshotProps;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  bugherdUrl: string;
  sessionReplayAvailable?: boolean;
  handleRegenerateScreenshot: (
    props: HandleRegenerateScreenshotProps
  ) => Promise<void> | void;
};

const isVideoUrl = (url: string) =>
  url.endsWith('.mp4') || url.endsWith('.webm');

const ExpandedScreenshot: FC<Props> = ({
  screenshot,
  videoRef,
  bugherdUrl,
  sessionReplayAvailable = false,
  handleRegenerateScreenshot
}) => {
  const {
    screenshotData,
    createdAt,
    retryScreenshotAt,
    taskId,
    projectId,
    orgId,
    currentUser,
    owner,
    billingRights,
    mobileScreenshots,
    isAdminView,
    container,
    projectDetailsUrl,
    url: taskUrl
  } = screenshot;

  const screenshotUrl = screenshotData?.url ?? null;
  const hasScreenshot = !!screenshotUrl;
  const isVideo = hasScreenshot && isVideoUrl(screenshotUrl);
  const isImage = hasScreenshot && !isVideo;
  const expectsScreenshot = !!(screenshotData?.screenshotKey || screenshotUrl);

  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const [showWhy, setShowWhy] = useState(false);
  const [activeMedia, setActiveMedia] = useState<MediaId>('screenshot');
  const [alertDismissed, setAlertDismissed] = useState(false);

  useEffect(() => {
    setZoomLevel(DEFAULT_ZOOM);
    setAlertDismissed(false);
    setShowWhy(false);
    setActiveMedia('screenshot');
  }, [taskId, screenshotUrl]);

  useEffect(() => {
    if (!sessionReplayAvailable && activeMedia === 'session') {
      setActiveMedia('screenshot');
    }
  }, [sessionReplayAvailable, activeMedia]);

  const dateNow = Date.now();
  const processingWaitPeriod = 300000;
  const isProcessing = !!(
    expectsScreenshot &&
    !hasScreenshot &&
    dateNow <
      new Date(retryScreenshotAt || createdAt).getTime() + processingWaitPeriod
  );

  const errorState = useScreenshotErrorState({
    screenshotData,
    isProcessing,
    retryScreenshotAt,
    apiDomain: bugherdUrl,
    projectId,
    taskId
  });

  const isUpgradeError = errorState.type === 'wrong_plan';
  const canSeeWhyModalLink = !(
    currentUser.role === 'guest' || currentUser.role === 'collaborator'
  );

  const imageStyle = useMemo(() => {
    if (zoomLevel === DEFAULT_ZOOM) {
      return undefined;
    }
    return {
      width: `${zoomLevel}%`,
      maxWidth: 'none' as const,
      maxHeight: 'none' as const
    };
  }, [zoomLevel]);

  const handleZoomIn = () =>
    setZoomLevel(level => Math.min(MAX_ZOOM, level + ZOOM_STEP));

  const handleZoomOut = () =>
    setZoomLevel(level => Math.max(MIN_ZOOM, level - ZOOM_STEP));

  const toggleWhy = (isOpen: boolean, _modalName?: string) => {
    setShowWhy(!isOpen);
  };

  const renderEmptyState = () => {
    if (!expectsScreenshot) {
      return (
        <div className={styles.emptyState}>
          <p className={styles.noScreenshot}>No screenshot</p>
        </div>
      );
    }

    if (isUpgradeError && !mobileScreenshots) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateInner}>
            <ScreenshotsUpgrade
              owner={owner}
              subscriptionUrl={`/settings/organizations/${orgId}/subscription`}
              hasBillingPermission={billingRights}
              container={container}
              isAdminView={isAdminView}
            />
          </div>
        </div>
      );
    }

    if (alertDismissed && !isProcessing) {
      return <div className={styles.emptyState} />;
    }

    const showGenerating = isProcessing;
    const alertType = showGenerating ? 'info' : 'warning';

    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateInner}>
          <Alert
            className={styles.screenshotAlert}
            type={alertType}
            message={
              <span className={styles.alertMessage}>
                {showGenerating && (
                  <div className={styles.processingContainer}>
                    <Loader className={styles.loader} />
                    <span>{strings.generating}</span>
                  </div>
                )}

                {!showGenerating && (
                  <span>
                    {strings.notCaptured}
                    {canSeeWhyModalLink && (
                      <a onClick={() => toggleWhy(showWhy)}>
                        ({strings.why})
                      </a>
                    )}
                  </span>
                )}

                <WhyModal
                  show={showWhy}
                  toggleWhy={toggleWhy}
                  orgId={orgId}
                  projectId={projectId}
                  taskId={taskId}
                  url={taskUrl}
                  screenshotError={errorState.type || 'generic'}
                  isSidebar
                  hasAccess
                  screenshotKey={screenshotData?.screenshotKey || ''}
                  canAccessBilling
                  container={container}
                />

                {errorState.retryData && !isUpgradeError && !showGenerating && (
                  <Button
                    className={styles.retry}
                    type="primary"
                    onClick={() =>
                      handleRegenerateScreenshot({
                        taskId,
                        screenshotData: errorState.retryData as FailedScreenshotData,
                        apiDomain: bugherdUrl,
                        projectId,
                        hasAccess: mobileScreenshots,
                        screenshotServerUrl: getScreenshotServerUrl(),
                        projectDetailsUrl,
                        screenshotKey: screenshotData?.screenshotKey
                      })
                    }
                    shape="round"
                    size="small"
                  >
                    {strings.retry}
                  </Button>
                )}
              </span>
            }
            closable={!showGenerating}
            showIcon={!showGenerating}
            onClose={() => setAlertDismissed(true)}
          />
        </div>
      </div>
    );
  };

  const renderStageContent = () => {
    if (isVideo) {
      return (
        <div className={styles.videoWrapper}>
          <Video
            isAdminView={isAdminView}
            url={screenshotUrl}
            key={screenshotUrl}
            videoRef={videoRef}
          />
        </div>
      );
    }

    if (isImage) {
      return (
        <div className={styles.viewport}>
          <img
            src={screenshotUrl}
            alt="Task screenshot"
            className={cx(styles.image, {
              [styles.imageZoomed]: zoomLevel !== DEFAULT_ZOOM
            })}
            style={imageStyle}
          />
        </div>
      );
    }

    return renderEmptyState();
  };

  const showingSession = activeMedia === 'session' && sessionReplayAvailable;

  return (
    <div className={styles.root}>
      <div className={styles.stage}>
        {showingSession ? (
          <div className={styles.sessionStage}>
            <SessionReplay
              available={sessionReplayAvailable}
              apiDomain={bugherdUrl}
              projectId={projectId}
              taskId={taskId}
            />
          </div>
        ) : (
          <>
            {isImage && (
              <Button.Group className={styles.zoomControls}>
                <Button
                  className={styles.zoomButton}
                  icon={<Plus className={styles.zoomIcon} />}
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= MAX_ZOOM}
                  aria-label="Zoom in"
                />
                <Button
                  className={styles.zoomButton}
                  icon={<Minus className={styles.zoomIcon} />}
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= MIN_ZOOM}
                  aria-label="Zoom out"
                />
              </Button.Group>
            )}
            {renderStageContent()}
          </>
        )}
      </div>

      <div className={styles.switcherBar}>
        <div className={styles.switcher} role="tablist" aria-label="Media">
          <button
            type="button"
            role="tab"
            aria-selected={activeMedia === 'screenshot'}
            aria-label="Screenshot"
            className={cx(styles.switcherOption, {
              [styles.switcherOptionActive]: activeMedia === 'screenshot'
            })}
            onClick={() => setActiveMedia('screenshot')}
          >
            {isImage ? (
              <img
                src={screenshotUrl}
                alt="Screenshot"
                className={styles.switcherThumb}
              />
            ) : (
              <span className={styles.switcherThumbPlaceholder}>
                <ImageOff className={styles.switcherThumbIcon} />
              </span>
            )}
          </button>
          {sessionReplayAvailable && (
            <>
              <span className={styles.switcherDivider} aria-hidden="true" />
              <button
                type="button"
                role="tab"
                aria-selected={activeMedia === 'session'}
                className={cx(styles.sessionOption, {
                  [styles.sessionOptionActive]: activeMedia === 'session'
                })}
                onClick={() => setActiveMedia('session')}
              >
                <Play className={styles.sessionIcon} />
                Session
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandedScreenshot;
